import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { API } from '@src/api';
import {
  ApiResponse,
  SuggestProgramEmailRequest,
  SuggestProgramType,
} from '@src/api.types';
import { useEffect, useState } from 'react';
import { SuggestProgram } from './SuggestProgram';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { Button } from '@uhg-abyss/web/ui/Button';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { loggedInAdvocateAtom, memberInContextAtom } from '@src/global-state';
import { useAtomValue } from 'jotai';

export type SuggestProgramFormProps = {
  programData: SuggestProgramType | null;
  setModelOpen: (isOpen: boolean) => void;
  isEdit: boolean;
  onSaveProgram: (program: SuggestProgramType) => void;
  listOfValidFields: string[];
};

export function SuggestProgramForm({
  programData,
  setModelOpen,
  isEdit,
  onSaveProgram,
  listOfValidFields,
}: SuggestProgramFormProps): React.JSX.Element {
  const [suggestedPrograms, setSuggestedPrograms] = useState<
    SuggestProgramType[]
  >([]);
  const [currentProgram] = useState<SuggestProgramType | null>(programData);

  const [isFormValid, setIsFormValid] = useState(false);

  const [validFields, setValidFields] = useState<string[]>(listOfValidFields);
  const { toast } = useToast();
  const loggedInAdvocate = useAtomValue(loggedInAdvocateAtom);
  const [emailAddress, setEmailAddress] = useState('');

  const style = {
    paddingTop: '15px',
    paddingBottom: '24px',
    minHeight: '140px',
  };

  const SuccessErrorMessage = (resp: ApiResponse<undefined> | null) => {
    if (resp?.status === 'success') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- typescript confused by type prop which is valid in Abyss V2
      toast.show({
        type: 'success',
        background: 'saturated',
        title: 'Success',
        icon: <IconSymbol icon="check" />,
        message: (resp?.data as unknown as string) || 'Programs submitted successfully',
      } as any);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- typescript confused by type prop which is valid in Abyss V2
      toast.show({
        message: (resp?.data as unknown as string) || 'An error occurred',
        type: 'error',
        title: 'Error',
      } as any);
    }
  };

  const validateProviderAndProgram = (field, value, message) => {
    if (!value) {
      form.setError(field, {
        type: 'manual',
        message: message,
      });
      removeFromValidFields(field);
    } else {
      form.clearErrors(field);
      addToValidFields(field);
    }
  };

  const removeFromValidFields = (field) => {
    setValidFields((l) => l.filter((f) => f !== field));
  };

  const addToValidFields = (field) => {
    if (!validFields.includes(field)) {
      setValidFields([...validFields, field]);
    }
  };

  useEffect(() => {
    if (validFields.length === 4) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  }, [validFields]);

  const form = useForm({
    defaultValues: {
      providerName: currentProgram?.providerCBOName,
      programName: currentProgram?.programName,
      programPhone: currentProgram?.programPhone,
      programStateCode: currentProgram?.stateCode,
      programWebsite: currentProgram?.programWebsite,
      programNotes: currentProgram?.notes,
      emailAddress: loggedInAdvocate?.email || '',
    },
  });

  const validatePhoneOrWebsite = (phone, website) => {
    const phoneRegex = /^\(?\d{3}\)?[-.●]?\d{3}[-.●]?\d{4}$/;
    const websiteRegex =
      /^(https?:\/\/|www\.)[\da-z.-]+\.[a-z]{2,6}(\/[\w.-]*)*\/?$/i;
    if (!phone && !website) {
      form.setError('programPhone', {
        type: 'manual',
        message: 'Program phone or program website is required',
      });
      form.setError('programWebsite', {
        type: 'manual',
        message: 'Program phone or program website is required',
      });
      removeFromValidFields('phoneWebsite');
    } else if (phone && !phoneRegex.test(phone)) {
      form.setError('programPhone', {
        type: 'manual',
        message: 'Invalid phone number format',
      });
      removeFromValidFields('phoneWebsite');
    } else if (website && !websiteRegex.test(website)) {
      form.setError('programWebsite', {
        type: 'manual',
        message: 'Invalid website URL format',
      });
      removeFromValidFields('phoneWebsite');
    } else {
      form.clearErrors('programPhone');
      form.clearErrors('programWebsite');
      addToValidFields('phoneWebsite');
    }
  };

  const validateStateCode = (stateCode) => {
    if (stateCode && stateCode.length === 2 && states.indexOf(stateCode) > 0) {
      form.clearErrors('programStateCode');
      addToValidFields('stateCode');
    } else {
      form.setError('programStateCode', {
        type: 'manual',
        message: 'State code is a required field',
      });
      removeFromValidFields('stateCode');
    }
  };

  const handleAddProgram = (e) => {
    e.preventDefault();
    setValidFields([]);
    const data = form.getValues();
    const newProgram: SuggestProgramType = {
      providerCBOName: data.providerName ?? '',
      programName: data.programName ?? '',
      programPhone: data.programPhone ?? '',
      programWebsite: data.programWebsite,
      stateCode: data.programStateCode ?? '',
      notes: data.programNotes,
      advocateEmail: data.emailAddress,
    };
    setSuggestedPrograms([...suggestedPrograms, newProgram]);

    // Clear form
    clearForm();
  };
  const clearForm = () => {
    form.setValue('providerName', '');
    form.setValue('programName', '');
    form.setValue('programPhone', '');
    form.setValue('programWebsite', '');
    form.setValue('programStateCode', '');
    form.setValue('programNotes', '');
    form.clearErrors();
  };

  const handleSubmit = async () => {
    const req: SuggestProgramEmailRequest = {
      programs: suggestedPrograms,
    };
    const resp = await API.submitPrograms(req);
    SuccessErrorMessage(resp);
  };

  const updateProgram = (newProgram: SuggestProgramType, index: number) => {
    const updatedPrograms = [...suggestedPrograms];
    updatedPrograms[index] = newProgram;
    setSuggestedPrograms(updatedPrograms);
  };

  const states: string =
    'AL|AK|AS|AZ|AR|CA|CO|CT|DE|DC|FM|FL|GA|GU|HI|ID|IL|IN|IA|KS|KY|LA|ME|MH|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|MP|OH|OK|OR|PW|PA|PR|RI|SC|SD|TN|TX|UT|VT|VI|VA|WA|WV|WI|WY';

  const handleSaveProgram = () => {
    const data = form.getValues();
    const savedProgram: SuggestProgramType = {
      providerCBOName: data.providerName ?? '',
      programName: data.programName ?? '',
      programPhone: data.programPhone ?? '',
      programWebsite: data.programWebsite,
      stateCode: data.programStateCode ?? '',
      notes: data.programNotes,
      advocateEmail: data.emailAddress,
    };
    onSaveProgram(savedProgram);
  };

  function isEmpty(obj) {
    for (const prop in obj) {
      if (Object.hasOwn(obj, prop)) {
        return false;
      }
    }

    return true;
  }
  const handleEmailAddress = (e) => {
    setEmailAddress(e.target.value);
    form.trigger('emailAddress');
  };
  return (
    <>
      <div style={{ paddingTop: '10px', paddingBottom: '10px' }}>
        {!isEdit && (
          <div>
            <Text>
              You can submit up to <b>25 resources</b> in this form.{' '}
              <b>
                If you have more than 25 resources to submit, please submit an
                addition form.
              </b>{' '}
              You will receive an email with an update on this request{' '}
              <b>within 7-10 business days.</b> Please note, to be added{' '}
              <b>
                CBO must offer free or reduced cost services and provide
                directly to the member.
              </b>
            </Text>
            <div>
              {suggestedPrograms?.map((program, index) => {
                return (
                  <SuggestProgram
                    // isEdit={isEdit}
                    originalProgramData={program}
                    onUpdateProgram={(prog) => {
                      updateProgram(prog, index);
                    }}
                    onDeleteProgram={(prog) => {
                      const updatedPrograms = suggestedPrograms.filter(
                        (p) => p !== prog
                      );
                      setSuggestedPrograms(updatedPrograms);
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
        <Layout.Group
          alignLayout="center"
          style={{
            marginTop: '5px',
            marginBottom: '5px',
            marginLeft: '5px',
            padding: '15px',
            border: 'solid #D3D3D3 1px',
            borderRadius: '10px',
          }}
        >
          <FormProvider state={form}>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', paddingBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <TextInput
                  label="Provider/CBO Name (required)"
                  value={currentProgram?.providerCBOName}
                  model="providerName"
                  isClearable
                  onClear={() => {
                    validateProviderAndProgram(
                      'providerName',
                      null,
                      'Provider name is required'
                    );
                  }}
                  onChange={(e) => {
                    validateProviderAndProgram(
                      'providerName',
                      e.target.value,
                      'Provider name is required'
                    );
                  }}
                  validators={{
                    required: 'Please enter provider/CBO name',
                  }}
                  subText="This is the name of the organization that provides the service (e.g. The Salvation Army)"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextInput
                  label="Program Name and Description (required)"
                  value={currentProgram?.programName}
                  model="programName"
                  descriptorsDisplay="column"
                  onChange={(e) => {
                    validateProviderAndProgram(
                      'programName',
                      e.target.value,
                      'Program name is required'
                    );
                  }}
                  isClearable
                  onClear={() => {
                    validateProviderAndProgram(
                      'programName',
                      null,
                      'Program name is required'
                    );
                  }}
                  validators={{
                    required: 'Please enter program name/description',
                  }}
                  subText="This is the name of the program or service (e.g. Salvation Army Food Pantry)"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', paddingBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <TextInput
                  label="Program Phone (required if no program website)"
                  model="programPhone"
                  isClearable
                  descriptorsDisplay="column"
                  onClear={() => {
                    validatePhoneOrWebsite(
                      null,
                      form.getValues()?.programWebsite
                    );
                  }}
                  onChange={(e) => {
                    validatePhoneOrWebsite(
                      form.getValues()?.programPhone,
                      form.getValues()?.programWebsite
                    );
                  }}
                  subText="This is the phone number of the program, please include area code"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextInput
                  label="Program Website (required if no program phone number)"
                  value={currentProgram?.programWebsite}
                  model="programWebsite"
                  isClearable
                  descriptorsDisplay="column"
                  onClear={() => {
                    validatePhoneOrWebsite(
                      form.getValues()?.programPhone,
                      null
                    );
                  }}
                  onChange={(e) => {
                    validatePhoneOrWebsite(
                      form.getValues()?.programPhone,
                      form.getValues()?.programWebsite
                    );
                  }}
                  subText="Only one URL per row. Please include www or http"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', paddingBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label>
                  <strong>State Code (required) </strong>
                  <span style={{ color: 'red' }}>*</span>
                </label>
                <TextInput
                  label=""
                  value={currentProgram?.stateCode}
                  model="programStateCode"
                  isClearable
                  onClear={() => {
                    validateStateCode(null);
                  }}
                  onChange={(e) => {
                    validateStateCode(e.target.value);
                  }}
                  subText="Must be the abbreviation of the state the provider or program is in (e.g. TX)"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextInputArea
                  label="Notes"
                  model="programNotes"
                  css={{ 'abyss-text-input-area': { fontFamily: 'inherit' } }}
                />
              </div>
            </div>
            <div style={{ paddingBottom: '8px' }}>
              <TextInput
                label="Submitter email (required)"
                model="emailAddress"
                onChange={handleEmailAddress}
                validators={{
                  required: 'Enter a valid email address',
                  pattern: {
                    value:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: 'Invalid Email',
                  },
                }}
                subText="This is the email of the person submitting the request"
              />
            </div>
          </FormProvider>
        </Layout.Group>
        {isEdit ? (
          <>
            <Button
              data-cy="save"
              type="submit"
              isDisabled={!isFormValid || !isEmpty(form.formState.errors)}
              style={{ width: 'auto', marginLeft: '15px', whiteSpace: 'nowrap' }}
              onClick={() => {
                setModelOpen(false);
                handleSaveProgram();
              }}
            >
              Add program
            </Button>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '15px', marginLeft: '15px', gap: '8px' }}>
            <Button
              data-cy="add-program"
              type="submit"
              style={{
                width: 'auto',
                whiteSpace: 'nowrap',
              }}
              onClick={handleAddProgram}
              isDisabled={
                !isFormValid ||
                !isEmpty(form.formState.errors) ||
                suggestedPrograms.length >= 25
              }
            >
              Add program
            </Button>
            <Button
              variant="text"
              color="brand"
              data-cy="clear-program"
              onClick={() => {
                clearForm();
              }}
            >
              Clear
            </Button>
            </div>
            <div style={{ padding: '8px 0px 8px 0px' }}>
              <Divider color="$primary1" />
            </div>
            <Layout.Group alignLayout="center">
              <Button
                type="submit"
                isDisabled={
                  !isEmpty(form.formState.errors) ||
                  suggestedPrograms.length === 0
                }
                style={{
                  // width: '20%',
                  marginTop: '15px',
                  marginLeft: '15px',
                }}
                onClick={() => {
                  handleSubmit();
                  setModelOpen(false);
                }}
              >
                Submit
              </Button>
            </Layout.Group>
          </>
        )}
      </div>
    </>
  );
}
